import React, { useEffect, useState } from "react";
import MapLocationSelector from "../../../components/mapLocationSelector/MapLocationSelector";
import ImageSelector from "../../../components/imageSelector/ImageSelector";
import styles from "./mythSummary.module.css";
import { app } from "../../../helpers/myFirebase";
import MythImageEditor from "../mythImageEditor/MythImageEditor";
import { WaveformGenerator } from "../../../components/waveformGenerator/WaveformGenerator";
import SavingModal from "../../../components/savingModal/SavingModal";

const db = app.firestore();
const storage = app.storage();

export default function MythSummary({ myth, setCurrMyth, onUpdate, onClose }) {
  const [isSaving, setIsSaving] = useState(false);
  const [currFileInfo, setCurrFileInfo] = useState(null);
  const [currEditInProgress, setCurrEditInProgress] = useState(null);

  const onTitleChange = (value) => {
    setCurrMyth({ ...myth, title: value });
  };

  const onExcerptChange = (value) => {
    let inputValue = value;
    if (inputValue.length > 150) {
      inputValue = inputValue.slice(0, 150);
    }

    setCurrMyth({ ...myth, excerpt: inputValue });
  };

  const onLocationChange = (newLocation) => {
    setCurrMyth({ ...myth, location: newLocation });
  };

  const onlocationPhotoFileSelect = (e) => {
    setCurrFileInfo({ file: e.target.files[0], type: "placePhoto" });
  };

  const onComicPhotoFileSelect = (e) => {
    setCurrFileInfo({ file: e.target.files[0], type: "comicPhoto" });
  };

  const onAudioFileSelect = (e) => {
    setCurrFileInfo({ file: e.target.files[0], type: "audio" });
  };

  const onUpload = async () => {
    if (!currFileInfo || !myth) return;

    setIsSaving(true);

    try {
      const { file, type } = currFileInfo;
      const storageRef = storage.ref();
      const fileRef = storageRef.child(file.name);
      await fileRef.put(file);
      db.collection("myths")
        .doc(myth.id)
        .update({
          [type]: {
            name: file.name,
            url: await fileRef.getDownloadURL(),
          },
        });

      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      alert(`Sorry, there was an error uploading the file: ${error}`);
    }
  };

  const onUploadAudio = async (waveformData) => {
    if (!currFileInfo || !myth) return;

    setIsSaving(true);

    try {
      const { file, type } = currFileInfo;
      const storageRef = storage.ref();
      const fileRef = storageRef.child(file.name);
      await fileRef.put(file);
      db.collection("myths")
        .doc(myth.id)
        .update({
          [type]: {
            name: file.name,
            url: await fileRef.getDownloadURL(),
            waveformData: waveformData,
          },
        });

      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      alert(`Sorry, there was an error uploading the file: ${error}`);
    }
  };

  const onComicImageUpdate = ({ canvas, editData }) => {
    if (!myth) return;

    canvas.toBlob(async (blob) => {
      var image = new Image();
      image.src = blob;
      const editedName = myth.comicPhoto.name.split(".").join("-edited.");

      const storageRef = storage.ref();
      const fileRef = storageRef.child(editedName);
      await fileRef.put(blob);
      db.collection("myths")
        .doc(myth.id)
        .update({
          "comicPhoto.editedUrl": await fileRef.getDownloadURL(),
          editData: editData,
        });
    });
  };

  return (
    <div className={styles.mythSummary}>
      {isSaving && <SavingModal />}

      <h2 className={styles.mythSummaryTitle}>
        <span>
          You are currently editing: <strong>{myth.title}</strong>
        </span>
        <button onClick={onClose} className="funButton cancel">
          Close
        </button>
      </h2>
      <TitleEditor
        label="Title:"
        textSrc={myth.title}
        isEditing={currEditInProgress === "title"}
        type="title"
        setIsEditing={setCurrEditInProgress}
        onTitleChange={onTitleChange}
        onSave={onUpdate}
      />
      <ExcerptEditor
        label="Description:"
        textSrc={myth.excerpt}
        isEditing={currEditInProgress === "excerpt"}
        type="excerpt"
        setIsEditing={setCurrEditInProgress}
        onExcerptChange={onExcerptChange}
        onSave={onUpdate}
      />
      <MapEditor
        label="Map Pointer:"
        latLng={myth.location}
        isEditing={currEditInProgress === "location"}
        type="location"
        setIsEditing={setCurrEditInProgress}
        onLocationChange={onLocationChange}
        onSave={onUpdate}
      />
      <PlacePhotoEditor
        label="Place Photo:"
        imgSrc={myth.placePhoto}
        isEditing={currEditInProgress === "placePhoto"}
        type="placePhoto"
        onlocationPhotoFileSelect={onlocationPhotoFileSelect}
        setIsEditing={setCurrEditInProgress}
        onSave={onUpload}
      />
      <ComicPhotoEditor
        label="Comic Pic:"
        imgSrc={myth.comicPhoto}
        isEditing={currEditInProgress === "comicPhoto"}
        type="comicPhoto"
        editData={myth.editData}
        onComicPhotoFileSelect={onComicPhotoFileSelect}
        onComicImageUpdate={onComicImageUpdate}
        setIsEditing={setCurrEditInProgress}
        onSave={onUpload}
      />
      <AudioEditor
        label="Audio:"
        audioSrc={myth.audio}
        isEditing={currEditInProgress === "audio"}
        type="audio"
        setIsEditing={setCurrEditInProgress}
        onAudioFileSelect={onAudioFileSelect}
        onSave={onUploadAudio}
      />
    </div>
  );
}

const TitleEditor = ({
  label,
  textSrc,
  onTitleChange,
  isEditing,
  setIsEditing,
  type,
  onSave,
}) => {
  const [hasUpdates, setHasUpdates] = useState(false);
  const [origValue, setOrigValue] = useState(null);

  useEffect(() => {
    if (!origValue) {
      setOrigValue(textSrc);
    }
  }, [textSrc, origValue]);

  const onCancelChange = () => {
    if (origValue) {
      onTitleChange(origValue);
    }
    setHasUpdates(false);
    setIsEditing(false);
  };

  const onChange = (e) => {
    setHasUpdates(true);
    onTitleChange(e.target.value);
  };

  return (
    <AssetEditor
      label={label}
      type={type}
      view={<span>{textSrc}</span>}
      edit={
        <input type="text" name="title" value={textSrc} onChange={onChange} />
      }
      onCancelChange={onCancelChange}
      onSave={onSave}
      isEditing={isEditing}
      hasUpdates={hasUpdates}
      setIsEditing={setIsEditing}
    />
  );
};

const ExcerptEditor = ({
  label,
  textSrc,
  onExcerptChange,
  isEditing,
  type,
  setIsEditing,
  onSave,
}) => {
  const [hasUpdates, setHasUpdates] = useState(false);

  const [origValue, setOrigValue] = useState(null);
  useEffect(() => {
    if (!origValue) {
      setOrigValue(textSrc);
    }
  }, [textSrc, origValue]);

  const onCancelChange = () => {
    if (origValue) {
      onExcerptChange(origValue);
    }
    setHasUpdates(false);
    setIsEditing(false);
  };

  const onChange = (e) => {
    setHasUpdates(true);
    onExcerptChange(e.target.value);
  };

  return (
    <AssetEditor
      label={label}
      type={type}
      view={<span>{textSrc}</span>}
      edit={<textarea rows={5} value={textSrc} onChange={onChange} />}
      onCancelChange={onCancelChange}
      onSave={onSave}
      isEditing={isEditing}
      hasUpdates={hasUpdates}
      setIsEditing={setIsEditing}
    />
  );
};

const MapEditor = ({
  label,
  latLng,
  onLocationChange,
  type,
  isEditing,
  setIsEditing,
  onSave,
}) => {
  const [hasUpdates, setHasUpdates] = useState(false);
  const [origValue, setOrigValue] = useState(null);
  useEffect(() => {
    if (!origValue) {
      setOrigValue(latLng);
    }
  }, [latLng, origValue]);

  const onCancelChange = () => {
    if (origValue) {
      onLocationChange(origValue);
    }
    setHasUpdates(false);
    setIsEditing(false);
  };

  const onChange = (newValue) => {
    setHasUpdates(true);
    onLocationChange(newValue);
  };

  return (
    <AssetEditor
      label={label}
      type={type}
      view={
        <MapLocationSelector
          startCoords={latLng}
          location={latLng}
          autoOpen={false}
          zoom={17}
        />
      }
      edit={
        <MapLocationSelector
          startCoords={latLng}
          setLocation={onChange}
          location={latLng}
          autoOpen={false}
          zoom={17}
          draggable={true}
        />
      }
      onSave={onSave}
      onCancelChange={onCancelChange}
      isEditing={isEditing}
      hasUpdates={hasUpdates}
      setIsEditing={setIsEditing}
    />
  );
};

const PlacePhotoEditor = ({
  label,
  imgSrc,
  isEditing,
  setIsEditing,
  type,
  onSave,
  onlocationPhotoFileSelect,
}) => {
  const [hasUpdates, setHasUpdates] = useState(false);
  const [selectedPhotoSrc, setSelectedPhotoSrc] = useState(null);

  const onCancelChange = () => {
    setSelectedPhotoSrc(null);
    setHasUpdates(false);
    setIsEditing(false);
  };

  const src = !imgSrc ? null : imgSrc.editedUrl ? imgSrc.editedUrl : imgSrc.url;

  const onChange = (e) => {
    setHasUpdates(true);
    var reader = new FileReader();

    reader.onload = function (e) {
      setSelectedPhotoSrc(e.target.result);
    };

    reader.readAsDataURL(e.target.files[0]);
    onlocationPhotoFileSelect(e);
  };

  return (
    <AssetEditor
      label={label}
      type={type}
      view={src ? <img src={src} alt="Location" /> : <div>Add a Photo</div>}
      edit={
        <ImageSelector
          onChange={onChange}
          selectedPhotoSrc={selectedPhotoSrc}
        />
      }
      onSave={onSave}
      editButtText={!src ? "Add Photo" : "Change Photo"}
      onCancelChange={onCancelChange}
      isEditing={isEditing}
      hasUpdates={hasUpdates}
      setIsEditing={setIsEditing}
    />
  );
};

const ComicPhotoEditor = ({
  label,
  imgSrc,
  isEditing,
  setIsEditing,
  type,
  onSave,
  onComicPhotoFileSelect,
  onComicImageUpdate,
  editData,
}) => {
  const [hasUpdates, setHasUpdates] = useState(false);
  const [isSelectingNew, setIsSelectingNew] = useState(false);
  const [selectedPhotoSrc, setSelectedPhotoSrc] = useState(null);

  const src = !imgSrc ? null : imgSrc.editedUrl ? imgSrc.editedUrl : imgSrc.url;

  const onCancelNewSelect = () => {
    setHasUpdates(false);
    setIsSelectingNew(false);
    setSelectedPhotoSrc(null);
  };

  const onCancelEdit = () => {
    setHasUpdates(false);
    setIsEditing(false);
  };

  const onChange = (e) => {
    setHasUpdates(true);
    var reader = new FileReader();

    reader.onload = function (e) {
      setSelectedPhotoSrc(e.target.result);
    };

    reader.readAsDataURL(e.target.files[0]);
    onComicPhotoFileSelect(e);
  };

  return (
    <div className={styles.mapInfo}>
      <div className={styles.info}>
        <div className={styles.infoLabel}>{label} </div>

        {!isEditing && !isSelectingNew && (
          <>
            {src && (
              <button
                className={`funButton ${styles.editAddButton}`}
                onClick={() => setIsEditing(type)}
              >
                Edit Crop
              </button>
            )}
            <button
              className={`funButton ${styles.editAddButton}`}
              onClick={() => setIsSelectingNew(true)}
            >
              {!src ? "Add Photo" : "Change Photo"}
            </button>
          </>
        )}

        {isSelectingNew && (
          <>
            <button
              className={`funButton ${styles.updateButton}`}
              disabled={!hasUpdates}
              onClick={onSave}
            >
              SAVE
            </button>
            <button
              className={`funButton cancel ${styles.updateButton}`}
              onClick={onCancelNewSelect}
            >
              CANCEL
            </button>
          </>
        )}
      </div>
      {!isEditing && !isSelectingNew && (
        <div className={styles.assetHolder}>
          {src ? <img src={src} alt="Comic" /> : <div>Add a Photo</div>}
        </div>
      )}
      {isEditing && (
        <div>
          <MythImageEditor
            sourceImgUrl={imgSrc ? imgSrc.url : null}
            editData={editData ? editData : defaultEditData}
            onImageUpdate={onComicImageUpdate}
            onCancel={onCancelEdit}
          />
        </div>
      )}

      {isSelectingNew && (
        <ImageSelector
          onChange={onChange}
          selectedPhotoSrc={selectedPhotoSrc}
        />
      )}
    </div>
  );
};

const AudioEditor = ({
  label,
  audioSrc,
  isEditing,
  setIsEditing,
  type,
  onSave,
  onAudioFileSelect,
}) => {
  const [hasUpdates, setHasUpdates] = useState(false);
  const [waveformData, setWaveformData] = useState(null);
  const [selectedAudioSrc, setSelectedAudioSrc] = useState(null);

  const src = audioSrc && audioSrc.url ? audioSrc.url : null;

  const onChange = (e) => {
    setHasUpdates(true);
    onAudioFileSelect(e);

    var reader = new FileReader();
    reader.onload = function (e) {
      setSelectedAudioSrc(e.target.result);
    };
    reader.readAsDataURL(e.target.files[0]);
  };

  const onCancelChange = () => {
    setHasUpdates(false);
    setIsEditing(false);
  };

  return (
    <div>
      <AssetEditor
        label={label}
        type={type}
        view={
          src ? (
            <audio src={audioSrc.url} controls />
          ) : (
            <div>Add an audio recording</div>
          )
        }
        edit={
          <div>
            <input
              type="file"
              className={styles.audioFileInput}
              onChange={onChange}
            />
            {selectedAudioSrc && (
              <WaveformGenerator
                totalBars={42}
                url={selectedAudioSrc}
                waveformData={waveformData}
                setWaveformData={setWaveformData}
              />
            )}
          </div>
        }
        onSave={() => onSave(waveformData)}
        onCancelChange={onCancelChange}
        isEditing={isEditing}
        editButtText={!src ? "Add Audio" : "Change Audio"}
        hasUpdates={hasUpdates}
        setIsEditing={setIsEditing}
      />
    </div>
  );
};

//
//
//
const AssetEditor = ({
  label,
  isEditing,
  setIsEditing,
  onSave,
  hasUpdates,
  type,
  view,
  edit,
  editButtText = "Edit",
  onCancelChange,
}) => {
  return (
    <div className={styles.mapInfo}>
      <div className={styles.info}>
        <div className={styles.infoLabel}>{label} </div>

        {!isEditing && (
          <button
            className={`funButton ${styles.editAddButton}`}
            onClick={() => setIsEditing(type)}
          >
            {editButtText}
          </button>
        )}

        {isEditing && (
          <>
            <button
              className={`funButton ${styles.updateButton}`}
              disabled={!hasUpdates}
              onClick={onSave}
            >
              SAVE
            </button>

            <button
              className={`funButton cancel ${styles.updateButton}`}
              onClick={onCancelChange}
            >
              CANCEL
            </button>
          </>
        )}
      </div>
      {!isEditing && <div className={styles.assetHolder}>{view}</div>}
      {isEditing && edit}
    </div>
  );
};

const defaultEditData = {
  cornerCoords: {
    topLeft: [0.0, 0.0],
    topRight: [1.0, 0.0],
    bottomLeft: [0.0, 1.0],
    bottomRight: [1.0, 1.0],
  },
  orientation: 1,
};
